/**
 * Home Page Component
 *
 * Landing page content for the National Council for Equal Shared Parenting
 * Contains all main sections between header and footer
 */

import React from 'react'
import { Box } from '@mui/material'
import { HeroSection } from '@/components/sections/HeroSection'
import { MissionSection } from '@/components/sections/MissionSection'
import { EmailSubscriptionSection } from '@/components/sections/EmailSubscriptionSection'
import { WhyEqualParentingSection } from '@/components/sections/WhyEqualParentingSection'
import { ImageCarouselSection } from '@/components/sections/ImageCarouselSection'
import { AffiliatesSection } from '@/components/sections/AffiliatesSection'
import SEOMetaTags from '@/components/SEOMetaTags'
import {
  heroContent,
  missionContent,
  emailSubscriptionContent,
  whyEqualParentingContent,
  imageCarouselContent,
} from '@/data'
import { affiliatesContent } from '@/data/affiliatesContent'
import { getSEOConfig, getCanonicalUrl } from '@/utils/seo'
import { generateOrganizationSchema } from '@/utils/schema'

/**
 * Home Page Component
 */
const Home: React.FC = () => {
  // Get SEO configuration for this page
  const seoConfig = getSEOConfig('/')!
  const canonicalUrl = getCanonicalUrl('/')

  // Generate Organization schema for search engines
  const organizationSchema = generateOrganizationSchema()

  // Scroll handlers
  const handleJoinMovement = () => {
    // Dialog is now opened directly in HeroSection component
    // No scrolling needed
  }

  const handleLearnMore = () => {
    // Navigate to leadership page
    window.location.href = '/leadership'
  }

  return (
    <Box component="main" role="main">
      {/* SEO Meta Tags */}
      <SEOMetaTags
        title={seoConfig.title}
        description={seoConfig.description}
        canonical={canonicalUrl}
        ogImage={seoConfig.ogImage}
        keywords={seoConfig.keywords}
        noIndex={seoConfig.noIndex}
        schemas={[organizationSchema]}
      />
      {/* Hero Section */}
      <HeroSection content={heroContent} onJoinClick={handleJoinMovement} />

      {/* Mission Section */}
      <MissionSection
        content={missionContent}
        onLearnMoreClick={handleLearnMore}
      />

      {/* Why Equal Parenting Section */}
      <WhyEqualParentingSection content={whyEqualParentingContent} />

      {/* Image Carousel Section */}
      <ImageCarouselSection content={imageCarouselContent} />

      {/* Affiliates Section */}
      <AffiliatesSection affiliates={affiliatesContent} />

      {/* Email Subscription Section */}
      <EmailSubscriptionSection content={emailSubscriptionContent} />
    </Box>
  )
}

export default Home
